import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import WhiteBgCard from "../../WhiteBgCard";
import AccountsDashboardCommonHeader from "../freshIntentSignals/AccountsDashboardCommonHeader";
import FollowUpCard from "./FollowUpCard";
import FollowUpActionModal from "./FollowUpActionModal";
import AccountsDashboardSeeAllModal from "../AccountsDashboardSeeAllModal";
import FollowUpReminderDrawer from "@/features/accounts/components/Mapping/FollowUpReminderDrawer";
import ShowDraftModal from "@/features/accounts/components/ShowDraftModal";
import { useDismissFollowUpSuggestion } from "@/features/accounts/hooks/useAccounts";
import { getAccountProfilePath } from "@/features/dashboard/utils/accountsDashboardUtils";

const FollowUp = ({ followUps = [], isLoading = false }) => {
  const [seeAllOpen, setSeeAllOpen] = useState(false);
  const [activeFollowUp, setActiveFollowUp] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState(false);
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    mutate: dismissSuggestion,
    isPending: isDismissing,
    variables: dismissing,
  } = useDismissFollowUpSuggestion();
  const items = (followUps ?? []).slice(0, 5);

  const refreshDashboard = () => {
    queryClient.invalidateQueries({ queryKey: ["dashboard-accounts"] });
    setActionModalOpen(false);
    setActiveFollowUp(null);
  };

  const handleFollowUpAction = (followUp) => {
    setActiveFollowUp(followUp);
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
  };

  const openReminderDrawer = () => {
    setActionModalOpen(false);
    setReminderDrawerOpen(true);
  };

  const openDraftModal = () => {
    setActionModalOpen(false);
    setDraftModalOpen(true);
  };

  const openAccount = () => {
    const path = getAccountProfilePath(activeFollowUp);
    if (!path) return;
    setActionModalOpen(false);
    navigate(path);
  };

  const closeFollowUpTools = () => {
    setReminderDrawerOpen(false);
    setDraftModalOpen(false);
    refreshDashboard();
  };

  return (
    <WhiteBgCard className="p-4 border-secondary dark:border-gray-800 bg-[#EBEFF1] dark:bg-gray-900/90">
      <div className="flex flex-col gap-2.5">
        <AccountsDashboardCommonHeader
          title="Follow Ups"
          tooltipContent="Scheduled reminders, overdue work, recommended account actions, and AI suggestions for accounts with follow-up alerts on."
          seeAllButtonText="See All"
          onSeeAll={() => setSeeAllOpen(true)}
        />
        <div className="max-h-[310px] overflow-y-auto overscroll-contain pr-1 flex flex-col gap-2.5">
          {isLoading ? (
            <div className="flex flex-col gap-2.5 w-full">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex flex-col gap-2.5 p-2.5 rounded-lg bg-white/70 dark:bg-gray-800/70 animate-pulse"
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="h-2.5 w-36 rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="h-2 w-20 rounded bg-gray-200 dark:bg-gray-700" />
                  </div>
                  <div className="h-7 w-full rounded-md bg-gray-200 dark:bg-gray-700" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="text-[0.8rem] text-gray-500 dark:text-gray-400 py-8 text-center min-h-[270px] w-full flex items-center justify-center">
              No follow-ups or recommendations.
            </p>
          ) : (
            items.map((followUp) => (
              <FollowUpCard
                key={`${followUp.follow_up_id}-${followUp.follow_up_type || followUp.item_type || "scheduled"}`}
                followUp={followUp}
                onAction={handleFollowUpAction}
                onDelete={(item) =>
                  dismissSuggestion({ suggestionId: item.follow_up_id })
                }
                isDeleting={
                  isDismissing &&
                  dismissing?.suggestionId === followUp.follow_up_id
                }
              />
            ))
          )}
        </div>
      </div>
      <AccountsDashboardSeeAllModal
        isOpen={seeAllOpen}
        onClose={() => setSeeAllOpen(false)}
        variant="follow_up"
        title="Follow-ups and recommendations"
        description="Scheduled work, overdue follow-ups, and recommended account actions ordered by priority."
        items={followUps}
      />
      <FollowUpActionModal
        followUp={activeFollowUp}
        isOpen={actionModalOpen}
        onClose={closeActionModal}
        onOpenReminder={openReminderDrawer}
        onOpenEmail={openDraftModal}
        onOpenAccount={openAccount}
        onCompleted={refreshDashboard}
      />
      <FollowUpReminderDrawer
        isOpen={reminderDrawerOpen}
        onClose={closeFollowUpTools}
        drawerType="followUpReminder"
        relationshipMappingId={activeFollowUp?.relation_contact_id}
        followUpToEdit={
          activeFollowUp?.item_type === "follow_up"
            ? {
                id: activeFollowUp.follow_up_id,
                channel: activeFollowUp.channel,
                scheduled_at: activeFollowUp.scheduled_at,
                notes: activeFollowUp.notes,
              }
            : null
        }
      />
      <ShowDraftModal
        isOpen={draftModalOpen}
        onClose={closeFollowUpTools}
        contact={
          activeFollowUp?.relation_contact_id
            ? {
                relationship_mapping_id: activeFollowUp.relation_contact_id,
                person_name: activeFollowUp.contact_name,
                company_name: activeFollowUp.company_name,
              }
            : null
        }
      />
    </WhiteBgCard>
  );
};

export default FollowUp;
